import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { money, formatApiError } from "@/lib/api";
import { PageHeader, Pagination } from "./AdminShared";
import { StatusBadge, fmtDate } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, Check, X, Eye } from "lucide-react";

export default function AdminOrders() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [view, setView] = useState(null);
  const [note, setNote] = useState("");

  const { data } = useQuery({
    queryKey: ["admin-orders", status, search, page],
    queryFn: async () => (await api.get("/admin/orders", { params: { status, search, page, limit: 10 } })).data,
  });

  const update = useMutation({
    mutationFn: async ({ id, payload }) => (await api.put(`/admin/orders/${id}`, payload)).data,
    onSuccess: () => {
      toast.success("Order updated");
      qc.invalidateQueries({ queryKey: ["admin-orders"] });
      qc.invalidateQueries({ queryKey: ["admin-stats"] });
      qc.invalidateQueries({ queryKey: ["admin-subs"] });
      setView(null); setNote("");
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const items = data?.items || [];

  return (
    <div>
      <PageHeader title="Orders" subtitle="Review, approve and manage customer orders" />
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input data-testid="order-search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Search by customer or package…" className="pl-10 bg-white/5 border-white/10 text-white" />
        </div>
        <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
          <SelectTrigger data-testid="order-status-filter" className="w-full sm:w-48 bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#0b0c12] border-white/10 text-white">
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="glass rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="text-slate-400">Customer</TableHead>
                <TableHead className="text-slate-400">Package</TableHead>
                <TableHead className="text-slate-400">Amount</TableHead>
                <TableHead className="text-slate-400">Payment</TableHead>
                <TableHead className="text-slate-400">Status</TableHead>
                <TableHead className="text-slate-400">Date</TableHead>
                <TableHead className="text-slate-400 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-10">No orders found.</TableCell></TableRow>
              ) : items.map((o) => (
                <TableRow key={o.id} className="border-white/10" data-testid={`admin-order-row-${o.id}`}>
                  <TableCell><div className="text-white font-medium">{o.user_name}</div><div className="text-xs text-slate-500">{o.user_email}</div></TableCell>
                  <TableCell className="text-slate-300">{o.package_name}<div className="text-xs text-slate-500">{o.duration_label}</div></TableCell>
                  <TableCell className="text-white">{money(o.amount, o.currency)}</TableCell>
                  <TableCell><StatusBadge status={o.payment_status} /></TableCell>
                  <TableCell><StatusBadge status={o.status} /></TableCell>
                  <TableCell className="text-slate-400 text-sm">{fmtDate(o.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-1 justify-end">
                      <Button data-testid={`order-view-${o.id}`} size="icon" variant="ghost" onClick={() => { setView(o); setNote(o.admin_note || ""); }} className="h-8 w-8 text-slate-300 hover:text-white"><Eye className="w-4 h-4" /></Button>
                      {o.status === "pending" && (
                        <>
                          <Button data-testid={`order-approve-${o.id}`} size="icon" variant="ghost" onClick={() => update.mutate({ id: o.id, payload: { status: "approved" } })} className="h-8 w-8 text-emerald-400 hover:text-emerald-300"><Check className="w-4 h-4" /></Button>
                          <Button data-testid={`order-reject-${o.id}`} size="icon" variant="ghost" onClick={() => update.mutate({ id: o.id, payload: { status: "rejected" } })} className="h-8 w-8 text-red-400 hover:text-red-300"><X className="w-4 h-4" /></Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <Pagination page={page} total={data?.total || 0} limit={10} onPage={setPage} />
      </div>

      <Dialog open={!!view} onOpenChange={(v) => !v && setView(null)}>
        <DialogContent className="bg-[#0b0c12] border-white/10 text-white">
          <DialogHeader><DialogTitle className="font-heading">Order Details</DialogTitle></DialogHeader>
          {view && (
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-slate-400">Customer</span><span className="text-white">{view.user_name} ({view.user_email})</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Package</span><span className="text-white">{view.package_name} · {view.duration_label}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Amount</span><span className="text-white">{money(view.amount, view.currency)}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Method</span><span className="text-white capitalize">{view.payment_method}</span></div>
              {view.payment_note && <div><span className="text-slate-400">Customer Note:</span><p className="text-white mt-1 bg-white/5 rounded p-2">{view.payment_note}</p></div>}
              <div className="pt-2">
                <Label className="text-slate-300">Admin Note</Label>
                <Textarea data-testid="order-admin-note" value={note} onChange={(e) => setNote(e.target.value)} className="mt-1 bg-white/5 border-white/10 text-white" />
              </div>
              <div className="flex gap-2 pt-1">
                <Select defaultValue={view.payment_status} onValueChange={(v) => setView({ ...view, payment_status: v })}>
                  <SelectTrigger data-testid="order-payment-select" className="bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0b0c12] border-white/10 text-white">
                    <SelectItem value="unpaid">Unpaid</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="refunded">Refunded</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 flex-wrap">
            <Button data-testid="order-save-approve" onClick={() => update.mutate({ id: view.id, payload: { status: "approved", payment_status: view.payment_status, admin_note: note } })} className="bg-emerald-600 hover:bg-emerald-500 text-white border-0">Approve</Button>
            <Button data-testid="order-save-reject" onClick={() => update.mutate({ id: view.id, payload: { status: "rejected", payment_status: view.payment_status, admin_note: note } })} className="bg-red-600 hover:bg-red-500 text-white border-0">Reject</Button>
            <Button data-testid="order-save-note" variant="outline" onClick={() => update.mutate({ id: view.id, payload: { status: view.status, payment_status: view.payment_status, admin_note: note } })} className="border-white/10 bg-white/5 text-white">Save Only</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
